import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { QueryClient, QueryClientProvider } from 'react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import localforage from 'localforage';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch on mount if data exists
      refetchOnReconnect: false, // Don't refetch on reconnect
      staleTime: 10 * 60 * 1000, // Consider data fresh for 10 minutes (increased from 5)
      cacheTime: 60 * 60 * 1000, // Cache for 1 hour (increased from 30 minutes)
      // Use cache-first strategy to reduce reads
      networkMode: 'offlineFirst', // Try cache first, then network
    },
    mutations: {
      // Don't refetch on mutation by default - use invalidateQueries instead
      retry: 1,
    },
  },
});

// Persist React Query cache across sessions (web and mobile) using async storage persister
const persister = createAsyncStoragePersister({
  storage: Capacitor.isNativePlatform()
    ? {
        getItem: (key) => Preferences.get({ key }).then(r => r.value ?? null),
        setItem: (key, value) => Preferences.set({ key, value }),
        removeItem: (key) => Preferences.remove({ key }),
      }
    : localforage,
  throttleTime: 1000,
});

persistQueryClient({
  queryClient,
  persister,
  maxAge: 24 * 60 * 60 * 1000,
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <ToastContainer
        position="top-right"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </QueryClientProvider>
  </React.StrictMode>
);